import type {Question, QuestionnaireValue} from '@refinio/one.models/lib/models/QuestionnaireModel.js';
import {classifyNeeds, formatProfileSummary, TAG_LABELS} from './needsClassification.js';

const RECIPIENT = 'mail@heyfreeda.com';

function formatValue(answer: QuestionnaireValue[]): string {
    if (answer.length === 0) return '\u2014';

    return answer
        .map(v => {
            if (v.valueCoding?.display) return v.valueCoding.display;
            if (v.valueString) return v.valueString;
            if (v.valueInteger) return v.valueInteger;
            if (v.valueDate) return v.valueDate;
            if (v.valueBoolean !== undefined) return v.valueBoolean ? 'Yes' : 'No';
            if (v.valueDecimal) return v.valueDecimal;
            return '';
        })
        .filter(Boolean)
        .join(', ');
}

/**
 * Build a plain-text representation of all answers (raw data section).
 */
function buildAnswerDump(
    groups: Question[],
    answers: Map<string, QuestionnaireValue[]>,
    language: string
): string {
    const lines: string[] = [
        `VOLLST\u00c4NDIGE ANTWORTEN (Sprache: ${language})`,
        ''
    ];

    for (const group of groups) {
        lines.push(`${(group.text ?? group.linkId).toUpperCase()}`);

        if (group.item) {
            for (const question of group.item) {
                if (question.type === 'display') continue;
                const answer = answers.get(question.linkId) ?? [];
                const value = formatValue(answer);
                const label = question.text ?? question.linkId;
                lines.push(`${label}`);
                lines.push(`\u2192 ${value}`);
                lines.push('');
            }
        }
    }

    return lines.join('\n');
}

/**
 * Build the full report: classification summary + raw answers.
 */
export function buildStructuredOutput(
    groups: Question[],
    answers: Map<string, QuestionnaireValue[]>,
    language: string
): string {
    const profile = classifyNeeds(answers);
    const summary = formatProfileSummary(profile, answers);
    const rawAnswers = buildAnswerDump(groups, answers, language);

    return summary + '\n\n' + rawAnswers;
}

/**
 * Build a short subject line with key tags for quick scanning in inbox.
 */
function buildSubjectLine(answers: Map<string, QuestionnaireValue[]>): string {
    const profile = classifyNeeds(answers);
    const name = answers.get('participantName')?.[0]?.valueString ?? 'Anonym';
    const date = new Date().toLocaleDateString('de-DE');

    const tagHints = profile.tags
        .slice(0, 3)
        .map(t => TAG_LABELS[t])
        .join(', ');

    const subject = tagHints
        ? `[Stufe 1] ${name} \u2014 ${tagHints} [${date}]`
        : `[Stufe 1] ${name} [${date}]`;

    return subject;
}

/**
 * Send questionnaire results via Formsubmit.co (no account needed).
 * Includes automated needs classification and recommended next steps.
 * Sends in the background — the user always sees the thank-you page.
 */
export function sendResultsEmail(
    groups: Question[],
    answers: Map<string, QuestionnaireValue[]>,
    language: string
): void {
    const body = buildStructuredOutput(groups, answers, language);
    const subject = buildSubjectLine(answers);

    const formData = new FormData();
    formData.append('email', RECIPIENT);
    formData.append('_subject', subject);
    formData.append('message', body);
    formData.append('_captcha', 'false');
    formData.append('_template', 'box');

    fetch(`https://formsubmit.co/ajax/${RECIPIENT}`, {
        method: 'POST',
        body: formData
    }).catch(err => console.error('Form submission failed:', err));
}
